import { useState } from 'react';
import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';

const CONTACT_EMAIL = 'support@asmakam.com';

export default function SmsOptIn() {
	const [phone, setPhone] = useState('');
	const [consent, setConsent] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitStatus, setSubmitStatus] = useState<
		'idle' | 'success' | 'error'
	>('idle');
	const [errorMessage, setErrorMessage] = useState('');

	const validatePhone = (phone: string): boolean => {
		const phoneRegex = /^\+?[\d\s\-\(\)]+$/;
		return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validatePhone(phone)) {
			setErrorMessage('Please enter a valid phone number');
			setSubmitStatus('error');
			return;
		}

		if (!consent) {
			setErrorMessage('Please agree to receive SMS messages');
			setSubmitStatus('error');
			return;
		}

		setIsSubmitting(true);
		setErrorMessage('');

		try {
			// Simulate API call with mock success
			await new Promise((resolve) => setTimeout(resolve, 1500));

			// Log the submission data for now
			console.log('SMS Opt-in Submission:', {
				phone: phone.trim(),
				consent: true,
				timestamp: new Date().toISOString(),
			});

			// Simulate successful submission
			setSubmitStatus('success');
			setPhone('');
			setConsent(false);
		} catch (error) {
			setErrorMessage('Failed to submit. Please try again later.');
			setSubmitStatus('error');
		} finally {
			setIsSubmitting(false);
		}
	};

	const formatPhoneInput = (value: string) => {
		const cleaned = value.replace(/\D/g, '');
		if (cleaned.length <= 3) return cleaned;
		if (cleaned.length <= 6)
			return `+${cleaned.slice(0, 1)} ${cleaned.slice(1, 4)} ${cleaned.slice(
				4
			)}`;
		return `+${cleaned.slice(0, 1)} ${cleaned.slice(1, 4)} ${cleaned.slice(
			4,
			7
		)} ${cleaned.slice(7, 11)}`;
	};

	const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const formatted = formatPhoneInput(e.target.value);
		setPhone(formatted);
	};

	return (
		<div className='bg-slate-950 text-white min-h-screen'>
			<Nav />
			<main className='mx-auto max-w-4xl px-6 py-20'>
				<h1 className='font-display text-3xl sm:text-5xl font-semibold tracking-tight text-white'>
					Join SMS Alerts — CrikIT / Asmakam
				</h1>
				<p className='mt-6 text-lg text-blue-100/90 leading-relaxed'>
					Enter your mobile number below to receive account alerts, event
					updates, and support messages from CrikIT / Asmakam LLC.
				</p>

				{submitStatus === 'success' && (
					<div className='mt-8 p-6 bg-green-900/30 border border-green-700/50 rounded-lg'>
						<h2 className='text-xl font-semibold text-green-300 mb-2'>
							Success!
						</h2>
						<p className='text-green-100/90'>
							You've been successfully signed up for SMS alerts. You'll receive
							a confirmation message shortly.
						</p>
					</div>
				)}

				{submitStatus === 'error' && errorMessage && (
					<div className='mt-8 p-6 bg-red-900/30 border border-red-700/50 rounded-lg'>
						<h2 className='text-xl font-semibold text-red-300 mb-2'>Error</h2>
						<p className='text-red-100/90'>{errorMessage}</p>
					</div>
				)}

				<form onSubmit={handleSubmit} className='mt-8 space-y-6'>
					<div>
						<label
							htmlFor='phone'
							className='block text-sm font-medium text-blue-100 mb-2'>
							Mobile Number (SMS)
						</label>
						<input
							type='tel'
							id='phone'
							name='phone'
							value={phone}
							onChange={handlePhoneChange}
							placeholder='+1 234 567 8900'
							required
							className='w-full px-4 py-3 bg-slate-900/50 border border-blue-800/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors'
						/>
					</div>

					<div className='space-y-4'>
						<label className='flex items-start space-x-3 cursor-pointer'>
							<input
								type='checkbox'
								id='consent'
								name='consent'
								checked={consent}
								onChange={(e) => setConsent(e.target.checked)}
								required
								className='mt-1 w-4 h-4 text-blue-600 bg-slate-900/50 border-blue-800/30 rounded focus:ring-blue-500 focus:ring-2'
							/>
							<span className='text-sm text-blue-100/90 leading-relaxed'>
								I agree to receive SMS messages from CrikIT / Asmakam LLC about
								account updates, event reminders, and support.
							</span>
						</label>

						<div className='text-sm text-blue-200/70 bg-slate-900/30 p-4 rounded-lg border border-blue-800/20'>
							<p>
								Msg & data rates may apply. Msg frequency varies. Reply{' '}
								<strong className='text-white'>STOP</strong> to unsubscribe,{' '}
								<strong className='text-white'>HELP</strong> for help.
							</p>
						</div>
					</div>

					<button
						type='submit'
						disabled={isSubmitting}
						className='w-full sm:w-auto px-8 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'>
						{isSubmitting ? 'Signing Up...' : 'Sign Up for SMS Alerts'}
					</button>
				</form>

				<div className='mt-12 pt-8 border-t border-blue-900/30'>
					<p className='text-sm text-blue-200/70'>
						By signing up, you agree to our{' '}
						<Link
							to='/terms'
							className='text-blue-400 underline hover:text-blue-300 transition-colors'>
							Terms & Conditions
						</Link>{' '}
						and{' '}
						<Link
							to='/privacy'
							className='text-blue-400 underline hover:text-blue-300 transition-colors'>
							Privacy Policy
						</Link>
						.
					</p>
				</div>

				<div className='mt-12 pt-8 border-t border-blue-900/30'>
					<h2 className='font-display text-2xl font-semibold tracking-tight mb-6 text-white'>
						Related
					</h2>
					<div className='space-y-2'>
						<Link
							to='/privacy'
							className='block text-blue-400 underline hover:text-blue-300 transition-colors'>
							Privacy Policy
						</Link>
						<Link
							to='/terms'
							className='block text-blue-400 underline hover:text-blue-300 transition-colors'>
							Terms & Conditions
						</Link>
					</div>
				</div>

				<div className='mt-12 pt-8 border-t border-blue-900/30'>
					<h2 className='font-display text-xl font-semibold tracking-tight mb-4 text-white'>
						Have Questions?
					</h2>
					<p className='text-blue-100/90'>
						Contact us at{' '}
						<a
							href={`mailto:${CONTACT_EMAIL}`}
							className='text-blue-400 hover:text-blue-300 transition-colors'>
							{CONTACT_EMAIL}
						</a>{' '}
						for any questions about our SMS program.
					</p>
				</div>
			</main>
			<Footer />
		</div>
	);
}
